// API functions for actions and action sub-resources.
// Actions use a flat route structure (not nested under categories).
// Permission filtering is done server-side via action category grants.

import type { ApiClient } from './client'

// --- Types ---

/** Action list response (summary fields). */
export interface Action {
  id: string
  title: string
  description?: string | null
  lifecycle_state: 'draft' | 'in_progress' | 'closed'
  category_id: string
  category_name: string
  owner_role_id: string
  owner_user_id?: string | null
  owner_display_name?: string | null
  short_code: string
  due_date?: string | null // YYYY-MM-DD
  is_overdue: boolean
  linked_risk_count: number
  linked_control_count: number
  linked_event_count: number
  update_count: number
  created_at: string
  updated_at: string
}

/** Action detail response (extends list with additional fields). */
export interface ActionDetail extends Action {
  created_via: string
  created_by: string
  closed_by?: string | null
  closed_at?: string | null
  closure_comment?: string | null
}

/** Action update (append-only comment). */
export interface ActionUpdate {
  id: string
  action_id: string
  update_text: string
  created_by: string
  created_by_name: string
  created_at: string
}

/** Linked control summary. */
export interface LinkedActionControl {
  link_id: string
  control_id: string
  control_name: string
  lifecycle_state: string
  category_name: string
  owner_display_name?: string | null
  linked_at: string
  linked_by: string
  linked_by_name: string
}

/** Linked event summary. */
export interface LinkedActionEvent {
  link_id: string
  event_id: string
  event_title: string
  lifecycle_state: string
  category_name: string
  linked_at: string
  linked_by: string
  linked_by_name: string
}

/** Linked risk summary. */
export interface LinkedActionRisk {
  link_id: string
  risk_id: string
  risk_name: string
  lifecycle_state: string
  register_name: string
  owner_display_name?: string | null
  linked_at: string
  linked_by: string
  linked_by_name: string
}

/** Evidence attached to an action. */
export interface ActionEvidence {
  id: string
  file_id: string
  file_name: string
  file_size_bytes: number
  mime_type: string
  download_url: string
  evidence_tag_id: string
  tag_name: string
  comment?: string | null
  is_archived: boolean
  archived_by?: string | null
  archived_at?: string | null
  uploaded_by: string
  uploaded_by_name: string
  uploaded_at: string
}

// --- Request types ---

export interface CreateActionRequest {
  title: string
  description?: string
  category_id: string
  owner_role_id: string
  due_date?: string
}

export interface UpdateActionRequest {
  title?: string
  description?: string
  due_date?: string
  change_notes: string
}

export interface CloseActionRequest {
  closure_comment: string
}

export interface PostUpdateRequest {
  update_text: string
}

// CreateEvidenceRequest removed — evidence upload now uses multipart form via uploadEvidence().

// --- List filters ---

export interface ActionListFilter {
  category_id?: string
  search?: string
  lifecycle_state?: string
  is_overdue?: boolean
  owner_user_id?: string
}

// --- Lifecycle state helpers ---

export const actionLifecycleStates = ['draft', 'in_progress', 'closed'] as const

export const actionLifecycleStateLabels: Record<string, string> = {
  draft: 'Draft',
  in_progress: 'In Progress',
  closed: 'Closed',
}

export const actionLifecycleStateColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-700',
  closed: 'bg-green-100 text-green-700',
}

// --- API functions ---

function buildFilterParams(filter?: ActionListFilter): string {
  if (!filter) return ''
  const params = new URLSearchParams()
  if (filter.category_id) params.set('category_id', filter.category_id)
  if (filter.search) params.set('search', filter.search)
  if (filter.lifecycle_state) params.set('lifecycle_state', filter.lifecycle_state)
  if (filter.is_overdue) params.set('is_overdue', 'true')
  if (filter.owner_user_id) params.set('owner_user_id', filter.owner_user_id)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export function actionsApi(api: ApiClient) {
  return {
    // Action CRUD
    list: (filter?: ActionListFilter) =>
      api.get<Action[]>(`/actions${buildFilterParams(filter)}`),
    create: (data: CreateActionRequest) => api.post<ActionDetail>('/actions', data),
    get: (actionId: string) => api.get<ActionDetail>(`/actions/${actionId}`),
    update: (actionId: string, data: UpdateActionRequest) =>
      api.put<ActionDetail>(`/actions/${actionId}`, data),

    // State transitions
    start: (actionId: string) =>
      api.post<ActionDetail>(`/actions/${actionId}/start`),
    close: (actionId: string, data: CloseActionRequest) =>
      api.post<ActionDetail>(`/actions/${actionId}/close`, data),

    // Updates feed
    listUpdates: (actionId: string) =>
      api.get<ActionUpdate[]>(`/actions/${actionId}/updates`),
    postUpdate: (actionId: string, data: PostUpdateRequest) =>
      api.post<ActionUpdate>(`/actions/${actionId}/updates`, data),

    // Cross-references
    controls: (actionId: string) =>
      api.get<LinkedActionControl[]>(`/actions/${actionId}/controls`),
    events: (actionId: string) =>
      api.get<LinkedActionEvent[]>(`/actions/${actionId}/events`),
    risks: (actionId: string) =>
      api.get<LinkedActionRisk[]>(`/actions/${actionId}/risks`),

    // Link/unlink
    linkRisk: (actionId: string, riskId: string) =>
      api.post<{ link_id: string }>(`/actions/${actionId}/risks`, { linked_to_id: riskId }),
    linkControl: (actionId: string, controlId: string) =>
      api.post<{ link_id: string }>(`/actions/${actionId}/controls`, { linked_to_id: controlId }),
    linkEvent: (actionId: string, eventId: string) =>
      api.post<{ link_id: string }>(`/actions/${actionId}/events`, { linked_to_id: eventId }),
    unlinkRisk: async (actionId: string, linkId: string, notes?: string) => {
      const response = await api.fetch(`/actions/${actionId}/risks/${linkId}`, {
        method: 'DELETE',
        ...(notes ? { body: JSON.stringify({ notes }) } : {}),
      })
      return response.json() as Promise<{ status: string }>
    },
    unlinkControl: async (actionId: string, linkId: string, notes?: string) => {
      const response = await api.fetch(`/actions/${actionId}/controls/${linkId}`, {
        method: 'DELETE',
        ...(notes ? { body: JSON.stringify({ notes }) } : {}),
      })
      return response.json() as Promise<{ status: string }>
    },
    unlinkEvent: async (actionId: string, linkId: string, notes?: string) => {
      const response = await api.fetch(`/actions/${actionId}/events/${linkId}`, {
        method: 'DELETE',
        ...(notes ? { body: JSON.stringify({ notes }) } : {}),
      })
      return response.json() as Promise<{ status: string }>
    },

    // Evidence
    evidence: (actionId: string, includeArchived?: boolean) =>
      api.get<ActionEvidence[]>(
        `/actions/${actionId}/evidence${includeArchived ? '?include_archived=true' : ''}`,
      ),
    uploadEvidence: async (actionId: string, params: { file: File; evidenceTagId: string; comment?: string }) => {
      const formData = new FormData()
      formData.append('file', params.file)
      formData.append('evidence_tag_id', params.evidenceTagId)
      if (params.comment) formData.append('comment', params.comment)
      const response = await api.fetch(`/actions/${actionId}/evidence`, {
        method: 'POST',
        body: formData,
      })
      return response.json() as Promise<ActionEvidence>
    },
    archiveEvidence: async (actionId: string, evidenceId: string) => {
      const response = await api.fetch(`/actions/${actionId}/evidence/${evidenceId}`, {
        method: 'PATCH',
      })
      return response.json() as Promise<{ status: string }>
    },

    // Move to a different category
    move: (actionId: string, destCategoryId: string, changeNotes?: string) =>
      api.post<ActionDetail>(`/actions/${actionId}/move`, {
        dest_category_id: destCategoryId,
        change_notes: changeNotes ?? '',
      }),
  }
}
